import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface App {
  id: string;
  name: string;
}

export interface Market {
  id: number;
  name: string;
  current_status: string;
}

export interface BetType {
  id: number;
  name: string;
  game_type: string;
  type: string;
}

export interface PayoutConfig {
  multiply_by: number;
  payout_description: string;
  min_amount: number;
  max_amount: number;
  min_payout: number;
  max_payout: number;
  is_enabled: boolean;
}

export interface Timestamps {
  created_at: string;
  updated_at: string;
}

export interface PayoutMultiplier {
  id: number;
  app: App;
  market: Market;
  bet_type: BetType;
  payout_config: PayoutConfig;
  timestamps: Timestamps;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
}

export interface Filters {
  search: string;
  app_id: string;
  market_id: string;
  bet_type_id: string;
}

export interface PayoutMultiplierState {
  payoutMultipliers: PayoutMultiplier[];
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  success: string | null;
  filters: Filters;
}

const initialState: PayoutMultiplierState = {
  payoutMultipliers: [],
  pagination: null,
  loading: false,
  error: null,
  success: null,
  filters: {
    search: '',
    app_id: '',
    market_id: '',
    bet_type_id: ''
  }
};

// Async thunks
export const fetchPayoutMultipliers = createAsyncThunk(
  'payoutMultiplier/fetchPayoutMultipliers',
  async (params: {
    page?: number;
    page_size?: number;
    search?: string;
    app_id?: string;
    market_id?: string;
    bet_type_id?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.app_id) queryParams.append('app_id', params.app_id);
      if (params.market_id) queryParams.append('market_id', params.market_id);
      if (params.bet_type_id) queryParams.append('bet_type_id', params.bet_type_id);

      const response = await api.get(`/admin/payout-multipliers/?${queryParams.toString()}`);
      console.log('Fetch Payout Multipliers API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Fetch Payout Multipliers API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updatePayoutMultiplier = createAsyncThunk(
  'payoutMultiplier/updatePayoutMultiplier',
  async (data: { id: number; [key: string]: any }, { rejectWithValue }) => {
    try {
      const { id, ...updateData } = data;
      const response = await api.patch(`/admin/payout-multipliers/${id}/`, updateData);
      console.log('Update Payout Multiplier API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Update Payout Multiplier API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createPayoutMultiplier = createAsyncThunk(
  'payoutMultiplier/createPayoutMultiplier',
  async (data: {
    app_id: string;
    market_id: number;
    bet_type_id: number;
    payout_multiplier: number;
    payout_description: string;
    min_amount: number;
    max_amount: number;
    min_payout: number;
    max_payout: number;
    is_enable: boolean;
    img_url?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/payout-multipliers/', data);
      console.log('Create Payout Multiplier API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Create Payout Multiplier API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deletePayoutMultiplier = createAsyncThunk(
  'payoutMultiplier/deletePayoutMultiplier',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/payout-multipliers/${id}/`);
      console.log('Delete Payout Multiplier API Response: Success');
      return id;
    } catch (error: any) {
      console.error('Delete Payout Multiplier API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const payoutMultiplierSlice = createSlice({
  name: 'payoutMultiplier',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch payout multipliers
    builder
      .addCase(fetchPayoutMultipliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayoutMultipliers.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutMultipliers = action.payload.data;
        state.pagination = action.payload.pagination;
        // Don't update filters from API response to prevent recursive calls
        // state.filters = action.payload.filters;
      })
      .addCase(fetchPayoutMultipliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update payout multiplier
    builder
      .addCase(updatePayoutMultiplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePayoutMultiplier.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Payout multiplier updated successfully';
        const updatedMultiplier = action.payload;
        const index = state.payoutMultipliers.findIndex(multiplier => multiplier.id === updatedMultiplier.id);
        if (index !== -1) {
          state.payoutMultipliers[index] = { ...state.payoutMultipliers[index], ...updatedMultiplier };
        }
      })
      .addCase(updatePayoutMultiplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create payout multiplier
    builder
      .addCase(createPayoutMultiplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPayoutMultiplier.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Payout multiplier created successfully';
        state.payoutMultipliers.unshift(action.payload);
      })
      .addCase(createPayoutMultiplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete payout multiplier
    builder
      .addCase(deletePayoutMultiplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePayoutMultiplier.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Payout multiplier deleted successfully';
        state.payoutMultipliers = state.payoutMultipliers.filter(
          multiplier => multiplier.id !== action.payload
        );
      })
      .addCase(deletePayoutMultiplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearError, clearSuccess } = payoutMultiplierSlice.actions;
export default payoutMultiplierSlice.reducer;
